<script lang="ts">
/** One removable row. */
export interface GaiaRemovableItem {
  key: string;
  label: string;
  /** Trailing chip, e.g. the evaluation's type. */
  meta?: string;
  /**
   * Leading glyph, e.g. the playbook action's type icon. Takes the `dot`'s
   * place when both are set — a row that can say what kind of thing it is
   * should, rather than repeating the same accent dot down the list.
   */
  icon?: Component;
}
</script>

<script setup lang="ts">
import { computed, type Component, type PropType } from 'vue';
import GaiaAddButton from '../base/GaiaAddButton.vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaSubPanel from './GaiaSubPanel.vue';
import { gaiaAccent, gaiaFontSize, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaRemovableItem[]>,
    required: true,
  },
  /** Wraps the rows in a labelled GaiaSubPanel; omit for bare rows. */
  label: {
    type: String,
    default: undefined,
  },
  /** Adds the trailing add-button to the panel head, e.g. "Add action". */
  addLabel: {
    type: String,
    default: undefined,
  },
  /** Shown instead of the rows when the list is empty. */
  emptyText: {
    type: String,
    default: undefined,
  },
  /**
   * What the row's ✕/trash actually does. A list of *members* detaches them
   * and keeps `remove`; a list that owns its items — a playbook's actions, a
   * table's columns — destroys them on confirm and must say `delete`, or the
   * row offers a reversible-looking ✕ for something permanent.
   */
  intent: {
    type: String as PropType<'delete' | 'remove'>,
    default: 'remove',
  },
  /** Accent dot before the label (actions); off for plain member rows. */
  dot: {
    type: Boolean,
    default: false,
  },
  /**
   * Show the rows without their edit and remove controls, and without the add
   * button — a list the viewer may read but not change. For a permission the
   * viewer does not hold: the membership is still worth seeing, so the list
   * stays rather than the whole section disappearing.
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /**
   * Ground of the wrapping panel, forwarded to GaiaSubPanel. `plain` is for a
   * list that already sits on a tinted parent — nesting tinted in tinted loses
   * the row edges (an outcome card's actions).
   */
  tone: {
    type: String as PropType<'tinted' | 'plain'>,
    default: 'tinted',
  },
  /**
   * Drop the panel box: the label and add-button sit under a hairline rule and
   * the rows lose their own chrome. For a list that is a section *of* the card
   * it sits in (an option's actions) rather than a block placed on it — a
   * bordered box inside a bordered card reads as one frame too many.
   */
  flush: {
    type: Boolean,
    default: false,
  },
  /**
   * Adds a pencil before the remove button. The mock's rows are remove-only,
   * but a row that stands for an editable record — a playbook action, an
   * evaluation assigned to a group — has nowhere else to be opened from.
   */
  editable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ add: []; remove: [key: string]; edit: [key: string] }>();

/** A label or an add-button means the rows sit in a panel, unless flushed. */
const paneled = computed(() => !props.flush && Boolean(props.label ?? props.addLabel));

const accent = gaiaAccent.base;
const rowBorder = gaiaNeutral.borderCard;
const chipBorder = gaiaNeutral.border;
const surface = gaiaNeutral.surface;
const soft = gaiaNeutral.surfaceSoft;
const text = gaiaNeutral.text;
const ink = gaiaNeutral.ink;
const faint = gaiaNeutral.textFaint;
const hover = gaiaNeutral.surfaceHoverStrong;
// `.label` carries state through a selector (`.bare .label` flips its size and
// colour), so its type reaches it through v-bind rather than GaiaText props.
const labelSize = gaiaFontSize.control;
const bareLabelSize = gaiaFontSize.dense;
</script>

<template>
  <!-- Removable rows with an optional add-button (Playbooks mock: an option's
       actions, and a group's assigned evaluations). Inside a labelled panel
       the rows are white; bare (unlabelled) rows sit on the soft surface. -->
  <component
    :is="paneled ? GaiaSubPanel : 'div'"
    :label="paneled ? props.label : undefined"
    :tone="paneled ? props.tone : undefined"
    :class="{ flush: props.flush }"
  >
    <template v-if="paneled && props.addLabel && !props.readonly" #actions>
      <gaia-add-button size="xs" :icon="false" @click="emit('add')">
        {{ props.addLabel }}
      </gaia-add-button>
    </template>
    <!-- The flush head is ours: GaiaSubPanel's own header comes with the box. -->
    <n-flex
      v-if="props.flush && (props.label || (props.addLabel && !props.readonly))"
      align="center"
      :size="8"
      :wrap="false"
      :style="{ marginBottom: '8px' }"
    >
      <gaia-text v-if="props.label" variant="label" size="nano" tone="subtle">
        {{ props.label }}
      </gaia-text>
      <gaia-add-button
        v-if="props.addLabel && !props.readonly"
        size="xs"
        :icon="false"
        :style="{ marginLeft: 'auto', flex: 'none' }"
        @click="emit('add')"
      >
        {{ props.addLabel }}
      </gaia-add-button>
    </n-flex>
    <n-flex vertical :size="6">
      <gaia-text v-if="!props.items.length && props.emptyText" size="meta" tone="faint">
        {{ props.emptyText }}
      </gaia-text>
      <div
        v-for="item in props.items"
        :key="item.key"
        class="row"
        :class="{ bare: !paneled && !props.flush, naked: props.flush }"
      >
        <n-icon v-if="item.icon" class="row-icon" :component="item.icon" :size="14" />
        <span v-else-if="props.dot" class="dot" />
        <span class="label">{{ item.label }}</span>
        <gaia-text v-if="item.meta" class="meta" size="meta" weight="semibold" tone="text">
          {{ item.meta }}
        </gaia-text>
        <button
          v-if="props.editable && !props.readonly"
          type="button"
          class="edit"
          :aria-label="`Edit ${item.label}`"
          @click="emit('edit', item.key)"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M11.2 2.8a1.4 1.4 0 0 1 2 2L6 12l-2.6.6L4 10z" />
          </svg>
        </button>
        <gaia-delete-button
          v-if="!props.readonly"
          :intent="props.intent"
          size="xs"
          :subject="item.label"
          @click="emit('remove', item.key)"
        />
      </div>
    </n-flex>
  </component>
</template>

<style scoped>
/* Stays CSS: `.row.bare` overrides the gap, which an n-flex writes inline. */
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 9px;
  border: 1px solid v-bind(rowBorder);
  border-radius: 8px;
  background: v-bind(surface);
}

/* The flush form is a section of its parent card: a rule above it, and rows
   that are just icon + name + controls. */
.flush {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid v-bind(rowBorder);
}

.row.naked {
  padding: 2px 0;
  border: none;
  border-radius: 0;
  background: none;
}

.row.bare {
  gap: 10px;
  padding: 9px 11px;
  border-radius: 10px;
  background: v-bind(soft);
}

.row-icon {
  flex: none;
  color: v-bind(accent);
}

.dot {
  width: 5px;
  height: 5px;
  flex: none;
  border-radius: 50%;
  background: v-bind(accent);
}

.label {
  min-width: 0;
  flex: 1;
  font-size: v-bind(labelSize);
  color: v-bind(text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bare .label {
  font-size: v-bind(bareLabelSize);
  color: v-bind(ink);
}

.meta {
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border: 1px solid v-bind(chipBorder);
  border-radius: 7px;
  background: v-bind(surface);
  white-space: nowrap;
}

/* Edit is not a destructive action, so it keeps the neutral hover — the
   remove alongside it is a GaiaDeleteButton and brings its own danger wash. */
.edit {
  width: 22px;
  height: 22px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: none;
  color: v-bind(faint);
  cursor: pointer;
}

.edit:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}
</style>
